import { AlertCircle, Download } from "lucide-react";
import type { ImportJob } from "@adva/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/v2/empty-state";
import { useDownloadUnmatchedCsv } from "@/hooks/use-import";

const SAMPLE_LIMIT = 25;

interface UnmatchedTabProps {
  job: ImportJob;
  disabled?: boolean;
}

export function UnmatchedTab({ job, disabled = false }: UnmatchedTabProps) {
  const total = job.unmatched_count;
  const download = useDownloadUnmatchedCsv();

  if (total === 0) {
    return (
      <EmptyState
        title="No unmatched rows"
        description="Every row matched its parent records."
      />
    );
  }

  const sample = job.unmatched_rows.slice(0, SAMPLE_LIMIT);
  const filename = `${job.entity_type}-unmatched-${job.id}.csv`;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/5 p-4">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-[color:var(--color-warning)]">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {total.toLocaleString()} rows held
        </div>
        <p className="typo-body text-[color:var(--color-text-md)]">
          These reference parent records that haven&rsquo;t been imported in
          this business yet. Import the parents first, then re-import this CSV.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <p className="typo-label text-[color:var(--color-text-md)]">
          Showing first {sample.length.toLocaleString()} of{" "}
          {total.toLocaleString()}
        </p>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled || download.isPending}
          onClick={() => download.mutate({ jobId: job.id, filename })}
        >
          <Download className="size-4" /> Download {total.toLocaleString()}{" "}
          unmatched rows (CSV)
        </Button>
      </div>

      <Table>
        <TableHeader>
          {/* ADV-1113 §6: disambiguate the relationship — "Row ID" is this
              row's own external_id, "Missing parent ID" is the unresolved FK
              value. The two share a root in some datasets, so the old
              "External ID / Value" headers read as duplicates. */}
          <TableRow>
            <TableHead className="w-20">Row</TableHead>
            <TableHead className="w-48">Row ID</TableHead>
            <TableHead className="w-48">Parent field</TableHead>
            <TableHead>Missing parent ID</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sample.map((r, i) => (
            <TableRow key={`${r.row}-${r.field}-${i}`}>
              <TableCell className="tabular-nums">{r.row}</TableCell>
              <TableCell className="font-mono text-[color:var(--color-text-md)]">
                {r.external_id || "—"}
              </TableCell>
              <TableCell className="font-mono text-[color:var(--color-text-md)]">
                {r.field}
              </TableCell>
              <TableCell className="font-mono text-[color:var(--color-text-md)]">
                {r.value || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
